import { useDebouncer } from '@tanstack/react-pacer';
import {
  type ChangeEvent,
  type ComponentType,
  createElement,
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { TextField, type TextFieldProps } from '../TextField/TextField';

type DebounceFieldElementProps = Omit<TextFieldProps, 'variant' | 'size' | 'dataTest' | 'designSystem'>;

export type DebounceFieldProps = Omit<TextFieldProps, 'onChange'> & {
  debounceTimeout?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  element?: ComponentType<DebounceFieldElementProps>;
};

// Pacer only reports keyed debouncers to its devtools, and never unregisters them — so key them in
// development only.
const isDevelopment = process.env.NODE_ENV === 'development';

export const DebounceField = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  debounceTimeout = 300,
  element,
  ...props
}: DebounceFieldProps) => {
  const [internalValue, setInternalValue] = useState(value ?? '');
  // Tracks the value we last told the consumer about, so the sync effect below can tell the
  // difference between an external reset (apply it) and the consumer simply echoing back the
  // value we just emitted (ignore it — internalValue may already be ahead of it from further typing).
  const lastEmittedRef = useRef(value ?? '');
  const id = useId();

  const debouncer = useDebouncer(
    (event: ChangeEvent<HTMLInputElement>) => {
      lastEmittedRef.current = event.target.value;
      onChange?.(event);
    },
    {
      wait: debounceTimeout,
      key: isDevelopment ? `DebounceField ${props['data-test'] ?? props.name ?? ''} ${id}` : undefined,
      onUnmount: d => d.flush(),
    }
  );

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value ?? '';
      setInternalValue(value ?? '');
    }
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInternalValue(event.target.value);
    debouncer.maybeExecute(event as ChangeEvent<HTMLInputElement>);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncer.flush();
    onBlur?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') debouncer.flush();
    onKeyDown?.(event);
  };

  if (element) {
    const { variant: _variant, size: _size, dataTest: _dataTest, designSystem: _designSystem, ...elementProps } = props;
    return createElement(element, {
      ...elementProps,
      value: internalValue,
      onChange: handleChange,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
    });
  }

  return (
    <TextField {...props} value={internalValue} onChange={handleChange} onBlur={handleBlur} onKeyDown={handleKeyDown} />
  );
};
